import React, { useEffect } from 'react';
import Seo from './Seo';
import './PrivacyPolicy.css';

// Effective date is deliberately a hard-coded constant, not a computed value —
// a policy's date must change only when the policy text changes.
const EFFECTIVE = 'August 27, 2026';
const SUPPORT = 'support@tudusmartplanner.com';
// Naming the operator is not decoration: GDPR Art. 13 and the equivalent rules
// elsewhere require the data controller to be identifiable, and Apple checks
// this page against the entity named on the App Store listing.
const OPERATOR = 'Anantam Intelligence Private Limited';

function PrivacyPolicy() {
  // /privacy#account-deletion is the URL given to Apple and Google as the
  // account-deletion link, so it has to land on that section. On a cold load
  // the browser resolves the hash before React has rendered anything, finds no
  // such element, and stays at the top — so scroll it ourselves after mount.
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;
    const target = document.getElementById(id);
    if (target) target.scrollIntoView({ behavior: 'auto', block: 'start' });
  }, []);

  return (
    <div className="legal-wrapper">
      <Seo
        path="/privacy"
        title="Privacy Policy — Tudu"
        description="How Tudu collects, uses, shares, and deletes your data, including account information, tasks, location, voice recordings, and scanned images."
      />
      <div className="legal-container">
        <header className="legal-header">
          <h1>Privacy <span className="brand-highlight">Policy</span></h1>
          <p className="legal-effective">Effective {EFFECTIVE}</p>
          <p className="legal-lede">
            Tudu is a task planner. To do its job it stores what you write down, and for
            some features it sends that content to services we do not control. This page
            says exactly what leaves your device, where it goes, and how to delete all of
            it. It covers the Tudu mobile apps (iOS and Android) and{' '}
            <strong>tudusmartplanner.com</strong>, all operated by{' '}
            <strong>{OPERATOR}</strong> — the company responsible for the data described
            below, and the one to contact about any of it.
          </p>
        </header>

        <section className="legal-section">
          <h2>1. What we collect</h2>

          <h3>Account information</h3>
          <p>
            When you create an account we store your email address, a username, and a
            password that is kept only as a salted hash — we never hold the password
            itself. You may optionally add a display name, a short bio, and a profile
            image.
          </p>
          <p>
            If you sign in with <strong>Google</strong>, we receive and store your Google
            account identifier, email address, and name. If you sign in with{' '}
            <strong>Apple</strong>, we receive and store Apple's opaque user identifier
            and — only on your very first sign-in, because Apple never sends it again —
            your email address and name. If you use Apple's Hide My Email option, we only
            ever see the relay address Apple generates, never your real one.
          </p>

          <h3>Content you create</h3>
          <p>
            Tasks and subtasks, routines and their schedules, categories, groups and tasks
            you share with other people, saved places, and coupons you add to your wallet.
            This is the substance of the app, and we keep it until you delete it.
          </p>

          <h3>Location</h3>
          <p>
            Only if you grant location permission. We use your coordinates to find nearby
            stores, places, and deals, and we store a coarse city, region, and country on
            your profile along with your most recent coordinates so that suggestions still
            work when the app reopens. You can decline the permission and every other part
            of Tudu continues to work; you can revoke it at any time in your device
            settings.
          </p>

          <h3>Voice recordings</h3>
          <p>
            Only when you use voice entry to add a task. The audio is recorded, sent for
            transcription, and the resulting text becomes your task. We do not record in
            the background, and we do not keep the audio after transcription.
          </p>

          <h3>Photos and camera images</h3>
          <p>
            Only when you choose to scan a handwritten list or a store flyer. The image is
            sent for text extraction and the extracted text becomes tasks or deals. We do
            not browse or index your photo library.
          </p>

          <h3>Device and notification data</h3>
          <p>
            A push notification token for each device you sign in on, so we can deliver
            reminders. Tokens are revoked when you sign out and deleted with your account.
          </p>
        </section>

        <section className="legal-section">
          <h2>2. Who we send your data to</h2>
          <p>
            These are the only third parties that receive your data, and each receives only
            what the listed feature requires.
          </p>
          <div className="legal-table-scroll">
            <table className="legal-table">
              <thead>
                <tr>
                  <th>Service</th>
                  <th>What it receives</th>
                  <th>Why</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>OpenAI</td>
                  <td>Task text, voice audio, scanned images</td>
                  <td>
                    Parsing what you type into structured tasks, transcribing voice entry,
                    reading scanned lists and flyers, breaking large tasks into steps, and
                    screening content for abuse
                  </td>
                </tr>
                <tr>
                  <td>Google Maps Platform</td>
                  <td>Coordinates and place search terms</td>
                  <td>Finding nearby stores and places, and resolving addresses</td>
                </tr>
                <tr>
                  <td>Google Sign-In</td>
                  <td>Authentication exchange only</td>
                  <td>Signing you in, if you choose Google</td>
                </tr>
                <tr>
                  <td>Sign in with Apple</td>
                  <td>Authentication exchange only</td>
                  <td>Signing you in, if you choose Apple</td>
                </tr>
                <tr>
                  <td>Firebase Cloud Messaging (Google)</td>
                  <td>Device push token and notification text</td>
                  <td>Delivering reminders to your device</td>
                </tr>
                <tr>
                  <td>Amadeus</td>
                  <td>Trip search terms, dates, destinations</td>
                  <td>Travel suggestions, only if you use the travel planner</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            Our own infrastructure is hosted on <strong>Render</strong> (application),{' '}
            <strong>Supabase</strong> (database), and <strong>Vercel</strong> (website).
            They store data on our behalf and do not use it for their own purposes.
          </p>
        </section>

        <section className="legal-section">
          <h2>3. What we do not do</h2>
          <ul className="legal-list">
            <li>We do not sell or rent your personal information, and we never have.</li>
            <li>
              We do not track you across other companies' apps or websites. Tudu contains
              no advertising SDK, no analytics SDK, and no third-party tracker of any kind.
            </li>
            <li>We do not show ads.</li>
            <li>We do not read your email, contacts, calendar, or messages.</li>
            <li>
              We do not use your content to train our own models, and we do not permit the
              services listed above to train theirs on it.
            </li>
          </ul>
        </section>

        <section className="legal-section" id="account-deletion">
          <h2>4. Deleting your account and your data</h2>
          <p className="legal-callout">
            <strong>In the Tudu app:</strong> open <strong>Profile</strong>, scroll to{' '}
            <strong>Delete Account</strong>, and confirm twice.
          </p>
          <p>
            Deletion is immediate and permanent. It removes your account and everything
            attached to it — tasks, subtasks, routines, group memberships, saved places,
            wallet coupons, category preferences, and every push notification token
            registered to you. It cannot be undone, and we cannot restore a deleted
            account.
          </p>
          <p>
            If you cannot reach the app, email <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>{' '}
            from the address on the account and we will delete it for you.
          </p>
          <p>
            Content you contributed to a shared group remains visible to the other members
            of that group, because it is also their record of the work. It is no longer
            linked to your account.
          </p>
        </section>

        <section className="legal-section">
          <h2>5. How long we keep things</h2>
          <p>
            Your content stays until you delete it or delete your account. Voice recordings
            are discarded after transcription — the audio is passed straight through and
            never written to our storage. Scanned images are discarded after text
            extraction. Push tokens are removed when you sign out of a device. Deleted
            records may persist for a short period in routine database backups before those
            backups are cycled out.
          </p>
        </section>

        <section className="legal-section">
          <h2>6. Your rights</h2>
          <p>
            Wherever you live, you can ask us for a copy of your data, ask us to correct
            it, or ask us to delete it. If you are in the European Economic Area or the
            United Kingdom you additionally have the right to object to processing, to
            restrict it, and to lodge a complaint with your data protection authority. If
            you are in California you have the rights described in the CCPA, including the
            right to know and the right to delete; note that we do not sell personal
            information, so there is nothing to opt out of.
          </p>
          <p>
            Write to <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a> and we will respond within
            30 days.
          </p>
        </section>

        <section className="legal-section">
          <h2>7. Security</h2>
          <p>
            Traffic between the app and our servers is encrypted in transit. Passwords are
            stored only as salted hashes. Access to the production database is restricted
            to the people who operate the service. No system is perfectly secure, and we do
            not claim otherwise — if a breach affects your data we will tell you.
          </p>
        </section>

        <section className="legal-section">
          <h2>8. Children</h2>
          <p>
            Tudu is not directed at children under 13, and we do not knowingly collect data
            from them. If you believe a child has created an account, email us and we will
            remove it.
          </p>
        </section>

        <section className="legal-section">
          <h2>9. Changes to this policy</h2>
          <p>
            If we change how we handle your data we will update this page and move the
            effective date at the top. Material changes will also be announced in the app
            before they take effect.
          </p>
        </section>

        <section className="legal-section">
          <h2>10. Contact</h2>
          <p>
            Tudu is operated by <strong>{OPERATOR}</strong>, which is the data controller
            for everything described on this page.
          </p>
          <p>
            Questions, requests, or complaints:{' '}
            <a href={`mailto:${SUPPORT}`}>{SUPPORT}</a>
          </p>
        </section>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
